/* eslint-disable no-redeclare */
/* eslint-disable no-case-declarations */
/* eslint-disable import/no-cycle */
/* eslint-disable no-undef */
import { Interpreter } from "xstate";
import { MediaContent } from "src/app/types/types.service";
import {
    ContentService,
    MatterportService,
    TagAction,
} from "@smarterplan/ngx-smarterplan-core";
import { PdfModalComponent } from "src/app/components/pdf-modal/pdf-modal.component";
import { VideoFullscreenComponent } from "src/app/components/video-fullscreen/video-fullscreen.component";
import { AudioComponent } from "src/app/components/audio/audio.component";
import { CarouselFullscreenComponent } from "src/app/components/carousel-fullscreen/carousel-fullscreen.component";
import { Logger } from "aws-amplify";
import { PhotonClient } from "../PhotonClient";
import { CameraPose, Event, Frame, ZoomData } from "../types";
import { makeSimulationFSM, FSMSchema, FSMEvent } from "./SimulationFSM";
import { RemoteTime } from "../time/remote-time";
import { RemoteControlService } from "../remote-control.service";

/**
 * The class controls the showcase via the sdk by consuming events from the sdk event source.
 */
export class RemoteSimulator {
    private fsm: Interpreter<unknown, FSMSchema, FSMEvent, any, any> = null;

    private sdk: any = null;

    private cameraPose: CameraPose = null;

    private cameraPoseSub: any = null;

    private zoomSub: any = null;

    private time: RemoteTime;

    private inTransition = false;

    private inRotation = false;

    private zooming = false;

    private lastFrame: Frame | null = null;

    logger: Logger;

    loggerInfo: { name: string; tourID: string; };

    pendingSweepID: string | null = null;

    zoom: ZoomData = null;

    constructor(
        private photonClient: PhotonClient,
        sdkToInject: any,
        private matterportService: MatterportService,
        private contentService: ContentService,
        private remoteService: RemoteControlService,
    ) {
        this.sdk = sdkToInject;
        this.onEnterIdle = this.onEnterIdle.bind(this);
        this.onEnterInitialized = this.onEnterInitialized.bind(this);
        this.onEnterSimulating = this.onEnterSimulating.bind(this);
        this.onExitSimulating = this.onExitSimulating.bind(this);
        this.onSweepExit = this.onSweepExit.bind(this);
        this.onTagHover = this.onTagHover.bind(this);
        this.onPoseEvent = this.onPoseEvent.bind(this);
        this.onTagAction = this.onTagAction.bind(this);

        this.fsm = makeSimulationFSM(
            this.onEnterIdle,
            this.onEnterInitialized,
            this.onEnterSimulating,
            this.onExitSimulating,
        );
        this.fsm.start();
        this.time = new RemoteTime(this.photonClient);
    }

    /**
     * Start the client simulator. This function does nothing if the simulator has already been started.
     */
    public start() {
        this.fsm.send("START");
    }

    /**
     * Stop the client simulator. This function does nothing if the simulaotr has already been stopped.
     */
    public stop() {
        this.fsm.send("STOP");
    }

    /**
     * Set Logger
     */
    setLogger(logger: Logger, loggerInfo: { name: string; tourID: string; }) {
        this.logger = logger;
        this.loggerInfo = loggerInfo;
    }

    private onEnterIdle = async () => { };

    private onEnterInitialized = async () => {
        this.fsm.send("SDK_READY");
        this.photonClient.events.on("sweep.exit", this.onSweepExit);
        this.photonClient.events.on("tag.hover", this.onTagHover);
        this.photonClient.events.on("pose", this.onPoseEvent);
        this.photonClient.events.on("tagAction", this.onTagAction);
        this.cameraPoseSub = this.sdk.Camera.pose.subscribe(
            (pose: CameraPose) => {
                this.cameraPose = pose;
            },
        );
        this.zoomSub = this.sdk.Camera.zoom.subscribe((zoom: ZoomData) => {
            this.zoom = zoom;
        });
    };

    private onEnterSimulating = async () => {
        this.inTransition = false;
        this.time.start();
    };

    private onExitSimulating = async () => {
        this.lastFrame = null;
        this.pendingSweepID = null;

        this.cameraPoseSub.cancel();
        this.cameraPoseSub = null;
        this.zoomSub.cancel();
        this.zoomSub = null;

        this.photonClient.events.off("sweep.exit", this.onSweepExit);
        this.photonClient.events.off("tag.hover", this.onTagHover);
        this.photonClient.events.off("pose", this.onPoseEvent);
        this.photonClient.events.off("tagAction", this.onTagAction);

        this.photonClient.events.removeAllListeners();
        this.remoteService.currentTagOnlineChange.next(null);
        this.time.stop();
        console.log("Remote simulator stopped");
    };

    onSweepExit = (currentEvent: Event) => {
        console.log("onSweepExit event", currentEvent);
        const sweepToGo = currentEvent.args[1];
        if (this.inRotation || this.inTransition) {
            console.log("setting pending sweep");
            this.pendingSweepID = sweepToGo;
            return;
        }
        console.log("starting moving cause host moved", currentEvent);
        this.goToSweep(sweepToGo);
    };

    onTagHover = (currentTagEvent: Event) => {
        console.log("onTagHover event", currentTagEvent);
        const elementID = currentTagEvent.args[0];
        this.remoteService.currentTagOnlineChange.next(elementID);
        if (elementID) {
            const { tag } =
                this.matterportService.getTagFromElementId(elementID);
            if (tag && !this.inTransition) {
                console.log("going to tag", tag);
                this.inTransition = true;
                this.sdk.Mattertag.navigateToTag(
                    tag,
                    "transition.instant",
                ).finally(() => {
                    // console.log("navigation to tag is over");
                    setTimeout(() => {
                        this.lastFrame = {
                            pose: this.cameraPose,
                            step: currentTagEvent.step,
                            zoom: this.zoom ? this.zoom.level : 1,
                        };
                        this.lastFrame.pose = this.cameraPose;
                        this.lastFrame.step = currentTagEvent.step;
                        this.inTransition = false;
                    }, 200); // to get the last frame set once arrived to tag
                });
            }
        }
    };

    onPoseEvent = (currentPoseEvent: Event) => {
        // console.log("onPoseEvent event", currentPoseEvent);
        const currentFrame = currentPoseEvent.args[0];
        if (
            currentFrame &&
            this.cameraPose &&
            !this.inTransition &&
            !this.inRotation
        ) {
            // Rotation update
            const diffX = Math.abs(
                this.cameraPose.rotation.x - currentFrame.pose.rotation.x,
            );
            const diffY = Math.abs(
                this.cameraPose.rotation.y - currentFrame.pose.rotation.y,
            );
            if (diffX > 0.06 || diffY > 0.06) {
                // console.log(
                //     "starting rotation: ",
                //     "diffY ",
                //     diffY,
                //     "; diffX ",
                //     diffX,
                // );
                this.inRotation = true;
                this.sdk.Camera.setRotation(
                    {
                        x: currentFrame.pose.rotation.x,
                        y: currentFrame.pose.rotation.y,
                    },
                    { speed: 100 },
                ).finally(() => {
                    this.lastFrame = currentFrame;
                    this.inRotation = false;
                    if (this.pendingSweepID) {
                        this.goToSweep(this.pendingSweepID);
                    }
                });
            }
            // zoom update
            const zoomDifference = this.zoom
                ? Math.abs(this.zoom.level - currentFrame.zoom)
                : 0.5;
            if (!this.zooming && zoomDifference > 0.09) {
                this.zooming = true;
                // console.log(
                //     "about to zoom from",
                //     this.zoom.level,
                //     " to ",
                //     currentFrame.zoom,
                // );
                this.sdk.Camera.zoomTo(currentFrame.zoom).finally(() => {
                    this.zooming = false;
                });
            }
        }
    };

    async goToSweep(sweepID: string) {
        this.inTransition = true;
        this.sdk.Sweep.moveTo(sweepID, {
            transition: this.sdk.Sweep.Transition.FLY,
            transitionTime: 1200,
        }).finally(() => {
            console.log("moved cause host moved", sweepID);
            this.inTransition = false;
            this.pendingSweepID = null;
        });
    }

    onTagAction = (currentTagAction: Event) => {
        const [action, data] = currentTagAction.args;
        console.log("onTagAction event", currentTagAction);
        switch (action) {
            case TagAction.AUDIO_CLICK:
                // data is audio comment ID
                this.contentService.presentAudioModal(
                    data as string,
                    AudioComponent,
                    "modal-padding",
                );
                break;
            case TagAction.VIDEO_CLICK:
                // data is MediaContent
                this.contentService.presentVideoModal(
                    data as MediaContent,
                    VideoFullscreenComponent,
                    "fullscreenModalClass",
                );
                break;
            case TagAction.YOUTUBE_CLICK:
                // data is MediaContent
                this.contentService.presentVideoModal(
                    data as MediaContent,
                    VideoFullscreenComponent,
                    "fullscreenModalClass",
                );
                break;
            case TagAction.IMAGE_CLICK:
                // data is featureID
                this.contentService.presentImageCarouselModal(
                    data,
                    CarouselFullscreenComponent,
                    "fullscreenModalClass",
                    true,
                    "0",
                );
                break;
            case TagAction.DOC_CLICK:
                // data is doc url
                this.contentService.presentPdfModal(
                    data,
                    PdfModalComponent,
                    "fullscreenModalClass",
                );
                break;
            case TagAction.CLOSE_MODAL:
                this.contentService.forceCloseCurrentModal();
                break;
            case TagAction.NEXT_SLIDE:
                this.contentService.moveToSlide(data);
                break;
            default:
                break;
        }
    };
}
